import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../../components/Card";
import Loader from "../../../components/partials/components/loader";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button, Col, Form, Row } from "react-bootstrap";
import { getApiCall } from "../../../service/httpCall";
import { API_AGENT_IDLE_STATS_GET } from "../../../service/url";
import { ROUTES, USER_ROLES } from "../../../utilities/constant";
import { getUser } from "../../../utilities/setting";

const normalizeIdleStats = (resp) => {
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.rows)) return resp.rows;
  return [];
};

const safeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatDuration = (seconds) => {
  const s = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const remSeconds = s % 60;

  const pad2 = (v) => String(v).padStart(2, "0");
  if (hours > 0) return `${hours}:${pad2(minutes)}:${pad2(remSeconds)}`;
  return `${minutes}:${pad2(remSeconds)}`;
};

function AgentIdleStatsList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [fromDateValue, setFromDateValue] = useState("");
  const [toDateValue, setToDateValue] = useState("");

  useEffect(() => {
    let isMounted = true;

    getUser()
      .then((user) => {
        if (!isMounted) return;
        setUserRole(user?.role || null);
        if (user?.role !== USER_ROLES.SUPERADMIN) {
          navigate(`/${ROUTES.DASHBOARD}`, { replace: true });
        }
      })
      .catch(() => {
        if (!isMounted) return;
        navigate(`/${ROUTES.DASHBOARD}`, { replace: true });
      });

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (userRole !== USER_ROLES.SUPERADMIN) return;

    let isMounted = true;
    setLoading(true);

    getApiCall(API_AGENT_IDLE_STATS_GET)
      .then((resp) => {
        if (!isMounted) return;
        setRows(normalizeIdleStats(resp));
      })
      .catch((error) => {
        console.error("Failed to fetch agent idle stats", error);
        if (!isMounted) return;
        setRows([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [userRole]);

  const tableRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((r, idx) => {
        const fromDate = safeDate(r?.from);
        const toDate = safeDate(r?.to);
        const durationSeconds =
          fromDate && toDate ? Math.max(0, Math.floor((toDate - fromDate) / 1000)) : 0;

        const agentName =
          r?.agent_name ||
          r?.agent?.agent_name ||
          r?.agent?.name ||
          r?.agent?.full_name ||
          r?.agent?.username ||
          "";

        return {
          key: r?.id || idx + 1,
          id: r?.id,
          agent_id: r?.agent_id,
          agent_name: agentName,
          from: fromDate ? fromDate.toLocaleString() : "",
          to: toDate ? toDate.toLocaleString() : "",
          _fromTs: fromDate ? fromDate.getTime() : 0,
          _toTs: toDate ? toDate.getTime() : 0,
          duration: formatDuration(durationSeconds),
        };
      })
      .sort((a, b) => (b._toTs || 0) - (a._toTs || 0));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    const start =
      fromDateValue
        ? new Date(`${fromDateValue}T00:00:00.000`).getTime()
        : null;
    const end =
      toDateValue
        ? new Date(`${toDateValue}T23:59:59.999`).getTime()
        : null;

    return tableRows.filter((r) => {
      if (query) {
        const agentId = r?.agent_id != null ? String(r.agent_id).toLowerCase() : "";
        const agentName = (r?.agent_name || "").toLowerCase();
        if (!agentId.includes(query) && !agentName.includes(query)) {
          return false;
        }
      }

      if (start != null && (r?._fromTs || 0) < start) return false;
      if (end != null && (r?._toTs || 0) > end) return false;

      return true;
    });
  }, [fromDateValue, searchValue, tableRows, toDateValue]);

  if (loading) return <Loader />;

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h4 className="card-title">Agent Idle Stats</h4>
      </Card.Header>
      <Card.Body>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Control
              placeholder="Search by ID or Name..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Control
              type="date"
              value={fromDateValue}
              onChange={(e) => setFromDateValue(e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Control
              type="date"
              value={toDateValue}
              onChange={(e) => setToDateValue(e.target.value)}
            />
          </Col>
          <Col md={2} className="d-flex">
            <Button
              variant="secondary"
              className="w-100"
              onClick={() => {
                setSearchValue("");
                setFromDateValue("");
                setToDateValue("");
              }}
            >
              Clear
            </Button>
          </Col>
        </Row>

        <DataTable value={filteredRows} paginator rows={10}>
          <Column field="agent_id" header="Agent ID" />
          <Column field="agent_name" header="Agent Name" />
          <Column field="from" header="From" />
          <Column field="to" header="To" />
          <Column field="duration" header="Duration" />
        </DataTable>
      </Card.Body>
    </Card>
  );
}

export default React.memo(AgentIdleStatsList);
